/**
 * Quote Request Summary Component
 * Slim version for workflow overview - shows essential quote info only
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  GetApp as DownloadIcon,
  Visibility as PreviewIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon
} from '@mui/icons-material';

import { temporaryOrderService, TemporaryOrder, PositionFile, TemporaryOrderPosition } from '../../../services/temporaryOrderService';
import { CaseData } from '../../../services/caseManagementService';
import { useNotification } from '../../../contexts/NotificationContext';

interface QuoteRequestSummaryProps {
  caseId: string;
  caseNumber?: string;
  caseData?: CaseData | null;
  onEmailAction?: () => void;
  onViewDetails?: () => void; // Callback to switch to full view
}

export const QuoteRequestSummary: React.FC<QuoteRequestSummaryProps> = ({
  caseId,
  caseNumber,
  caseData,
  onEmailAction,
  onViewDetails
}) => {
  const [temporaryOrder, setTemporaryOrder] = useState<TemporaryOrder | null>(null);
  const [loading, setLoading] = useState(true);
  
  const { showNotification } = useNotification();

  useEffect(() => {
    loadTemporaryOrder();
  }, [caseId]);

  const loadTemporaryOrder = async () => {
    setLoading(true);
    try {
      const response = await temporaryOrderService.getTemporaryOrderByCase(caseId);
      if (response.success && response.data) {
        setTemporaryOrder(response.data);
      } else {
        showNotification(response.error || 'Failed to load quote request details', 'error');
      }
    } catch (error: any) {
      showNotification(`Error loading quote request: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileDownload = (file: PositionFile) => {
    const filePath = file.file_path || `uploads/unified/${file.filename}`;
    const url = temporaryOrderService.getFileDownloadUrl(filePath);
    window.open(url, '_blank');
  };

  const getTotalFilesCount = () => {
    if (!temporaryOrder) return 0;
    return temporaryOrderService.getTotalFilesCount(temporaryOrder);
  };

  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'warning';
      case 'ready': return 'info';
      case 'sent': return 'primary';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={2}>
        <CircularProgress size={20} />
        <Typography variant="body2" sx={{ ml: 1 }}>
          Loading quote summary...
        </Typography>
      </Box>
    );
  }

  if (!temporaryOrder) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        No quote request found for case {caseNumber || caseId}
      </Alert>
    );
  }

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Quote Request Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {temporaryOrderService.formatOrderType(temporaryOrder.order_type)} • Due: {new Date(temporaryOrder.due_date).toLocaleDateString('de-DE')}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={loadTemporaryOrder}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={onViewDetails}
            >
              View Details
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2}>
          {/* Quick Stats */}
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="caption" color="text.secondary">Status & Positions</Typography>
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <Chip 
                  label={temporaryOrder.status.toUpperCase()} 
                  color={getOrderStatusColor(temporaryOrder.status) as any}
                  size="small"
                />
                <Typography variant="body2">
                  {temporaryOrder.positions.length} positions
                </Typography>
                <Typography variant="body2" display="flex" alignItems="center">
                  {getTotalFilesCount()}
                  <AttachFileIcon sx={{ ml: 0.5, fontSize: 14 }} />
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Supplier Info */}
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="caption" color="text.secondary">Suppliers</Typography>
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <BusinessIcon color="primary" sx={{ fontSize: 16 }} />
                {caseData?.metadata?.suppliers && Array.isArray(caseData.metadata.suppliers) ? (
                  <Typography variant="body2">
                    {caseData.metadata.suppliers.length} supplier{caseData.metadata.suppliers.length !== 1 ? 's' : ''} selected
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No suppliers assigned
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Positions Summary Table */}
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Position Overview
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ py: 1 }}>Pos.</TableCell>
                    <TableCell sx={{ py: 1 }}>Part Details</TableCell>
                    <TableCell align="right" sx={{ py: 1 }}>Qty</TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>Files</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {temporaryOrder.positions.slice(0, 3).map((position: TemporaryOrderPosition) => (
                    <TableRow key={position.id}>
                      <TableCell sx={{ py: 0.5 }}>
                        <Typography variant="caption" fontWeight="bold">
                          #{position.position_number}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <Box>
                          {position.part_name && (
                            <Typography variant="caption" fontWeight="medium">
                              {position.part_name}
                            </Typography>
                          )}
                          {position.part_number && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {position.part_number}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}>
                        <Typography variant="caption">{position.quantity}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        {position.files && position.files.length > 0 ? (
                          <Box display="flex" justifyContent="center">
                            {position.files.slice(0, 2).map((file: PositionFile) => (
                              <Tooltip key={file.id} title={file.original_filename}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleFileDownload(file)}
                                  sx={{ p: 0.25 }}
                                >
                                  <DownloadIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                              </Tooltip>
                            ))}
                            {position.files.length > 2 && (
                              <Typography variant="caption" color="text.secondary">
                                +{position.files.length - 2}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {temporaryOrder.positions.length > 3 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          ... and {temporaryOrder.positions.length - 3} more positions
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Special Instructions */}
          {temporaryOrder.notes && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Special Instructions</Typography>
              <Typography variant="body2" sx={{ 
                mt: 0.5, 
                fontStyle: 'italic',
                maxHeight: '3em',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {temporaryOrder.notes}
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default QuoteRequestSummary;
